import React, { useState } from "react";
import { Alert, Box, MenuItem, TextField } from "@mui/material";
import Button from "components/basic/button";
import LoadingSpinner from "components/basic/loadingSpinner";
import Table, { ColumnProps } from "components/basic/table";
import Typography from "components/basic/typography";
import { AdminVendorFilter } from "generated/graphql";
import useUrlParamState from "hooks/useUrlParamState";

import ListToolbar from "../ListToolbar";
import Pager from "../Pager";
import VendorDetail from "./components/VendorDetail";
import VendorStatusBadges from "./components/VendorStatusBadges";
import { useAdminVendors } from "../../adminApi";
import { VENDOR_FILTER_LABELS } from "../../constants";
import { formatDate } from "../../helpers";
import { AdminVendor } from "../../types";

interface VendorsTabProps {
  // Keeps the tab badges in step after an approval or verification.
  onChanged: () => void;
}

const VendorsTab: React.FC<VendorsTabProps> = ({ onChanged }) => {
  const [search, setSearch] = useState("");
  const [filterParam, setFilterParam] = useUrlParamState(
    "filter",
    AdminVendorFilter.All,
  );
  const [page, setPage] = useState(0);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const filter = filterParam as AdminVendorFilter;
  const { vendors, total, loading, error, refresh } = useAdminVendors(
    search,
    filter,
    page,
  );
  const selected = vendors.find((v) => v.id === selectedId) ?? null;

  const columns: ColumnProps<AdminVendor>[] = [
    {
      header: "Business",
      align: "left",
      card: "primary",
      cell: (v) => (
        <Box>
          <Typography variant="body1" style={{ fontWeight: 600 }}>
            {v.vendor.businessName}
          </Typography>
          <Typography variant="caption">
            {v.ownerName ?? "—"} ·{" "}
            {v.ownerEmail ?? v.vendor.primaryContactEmail}
          </Typography>
        </Box>
      ),
    },
    { header: "Province", cell: (v) => v.vendor.province },
    {
      header: "Joined",
      cell: (v) => formatDate(v.vendor.createdAt),
    },
    {
      header: "Status",
      card: "inline",
      cell: (v) => <VendorStatusBadges vendor={v} />,
    },
    {
      header: "",
      card: "actions",
      cell: (v) => (
        <Button size="small" onClick={() => setSelectedId(v.id)}>
          Review
        </Button>
      ),
    },
  ];

  return (
    <Box>
      <ListToolbar
        placeholder="Search business name or email"
        onSearch={(value) => {
          setSearch(value);
          setPage(0);
        }}
      >
        <TextField
          select
          size="small"
          value={filter}
          onChange={(e) => {
            setFilterParam(e.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 220 }}
        >
          {Object.values(AdminVendorFilter).map((value) => (
            <MenuItem key={value} value={value}>
              {VENDOR_FILTER_LABELS[value]}
            </MenuItem>
          ))}
        </TextField>
      </ListToolbar>

      {error ? (
        <Box px={3}>
          <Alert severity="error">{error}</Alert>
        </Box>
      ) : loading ? (
        <LoadingSpinner />
      ) : vendors.length === 0 ? (
        <Box p={4} textAlign="center">
          <Typography variant="body2">No vendors match.</Typography>
        </Box>
      ) : (
        <>
          <Table columns={columns} data={vendors} />
          <Pager page={page} total={total} onPageChange={setPage} />
        </>
      )}

      {selected && (
        <VendorDetail
          vendor={selected}
          onChanged={() => {
            refresh();
            onChanged();
          }}
          onClose={() => setSelectedId(null)}
        />
      )}
    </Box>
  );
};

export default VendorsTab;
